import { Check, TimeSpan } from "@sparkware/app-utils";
import { ServiceBase } from "../ServiceBase";
import { IIdleRecommendationsService, IIdleRecommendationsServiceParams, IIdleRecomToastDef } from "./idle-recommendations.service.interface";
import { IServiceFactoryExtended } from "../service-factory-extended.interface";
import { GameInfo } from "../games/game-info";
import { IdleRecommendationComponent } from "../../components/idle-recommendation/idle-recommendation.component";
import { makeObservable, observable } from "mobx";


const MAX_APPEARANCES_PER_SESSION: number = 3;
const ACTIVITY_DETECTION_INTERVAL: TimeSpan = TimeSpan.fromSeconds(1);
const IDLE_DETECTION_INTERVAL: TimeSpan = TimeSpan.fromMinutes(1);
const IDLE_RECOMMENDATIONS_LOCAL_STORAGE_KEY: string = 'idleRecommendationsData';
const DISPLAY_INTERVAL: TimeSpan = TimeSpan.fromSeconds(0);


export class IdleRecommendationsService extends ServiceBase implements IIdleRecommendationsService {
    static CLASS = 'IdleRecommendationsService';

    private _idleDetectionInterval: TimeSpan;
    private _numberOfAppearancesInSession: number;
    private _maxAppearancesAllowedPerSession: number;
    private _idleDetectionSubscription: ReturnType<typeof setInterval>;
    private _userActivityDetector: any;
    private _isIdleRecToasterDisplayed: boolean;
    private _displayInterval: TimeSpan;
    private _recommendedGameType: string | null = null;
    private _toasterDisposer: (() => void) | null = null;

    @observable.ref
    private _isThereRunningApps = true;

    constructor(services: IServiceFactoryExtended, params?: IIdleRecommendationsServiceParams) {
        super(services);

        makeObservable(this);

        this.toggleFeatureOn(params);

        this.services.appLauncher.then(appLauncher => {
            this.reaction(() => appLauncher.isThereRunningApps, (isThereRunningApps) => {
                this._isThereRunningApps = isThereRunningApps;
            }, { fireImmediately: true })
        });

        this.reaction(() => this.services.user.isAuthorized,
            () => {
                if (!this.services.user.isAuthorized) {
                    this._resetAppearancesInSession();
                }
                this._startIdleDetection();
            }, {
            fireImmediately: true
        });
    }


    public showToast(toastConfig: IIdleRecomToastDef) {
        return this.services.toaster.showCustomToast({
            id: toastConfig.id || "idle_recommendation_toast",
            toastOptions: toastConfig.toastOptions,
            renderContent: (handler) => {
                return <IdleRecommendationComponent key={'IdleRecommendation'} {...toastConfig.contentProps} />
            },
            onToastShown: toastConfig.onToastShown,
            onToastClosed: toastConfig.onToastClosed,
            displayCondition: () => !this._isThereRunningApps
        });
    }

    toggleFeatureOn(params) {
        this._idleDetectionInterval = params?.idleDetectionInterval ? TimeSpan.parse(params.idleDetectionInterval) : IDLE_DETECTION_INTERVAL;
        this._maxAppearancesAllowedPerSession = params?.maxAppearancesPerSession ? params.maxAppearancesPerSession : MAX_APPEARANCES_PER_SESSION;
        this._numberOfAppearancesInSession = this._getNoOfAppearancesInSessionFromLocalStorage();
        this._displayInterval = params?.displayInterval ? TimeSpan.parse(params.displayInterval) : DISPLAY_INTERVAL;
        this._subscribe();
        this._stopIdleDetection(); // Just to be on the safe side
        this._startIdleDetection();
    }

    private _subscribe() {
        this.services.appLauncher.then(appLauncher => {
            if (appLauncher.isThereRunningApps) {
                this._startIdleDetection();
            }
        });

        this.services.sdk.events.onGameClose(this._resetIdleDetectionTimer.bind(this));
        this.services.sdk.events.onGameOpen(this._closeToasterWhenOpeningAnotherGame.bind(this));
    }

    private _startIdleDetection() {
        if (this.services.user.isAuthorized) {
            this._startUserIdleDetection();
            this._startUserActivityDetection();
        } else {
            this._stopIdleDetection();
        }
    }

    private _disposeIdleDetectionTimer() {
        if (this._idleDetectionSubscription) {
            clearInterval(this._idleDetectionSubscription)
        }
    }

    private _resetIdleDetectionTimer() {
        this._disposeIdleDetectionTimer();
        this._startUserIdleDetection();
    }

    private _startUserIdleDetection() {
        this._idleDetectionSubscription = setInterval(() => {
            this._showIdleRecommendationToaster();
        }, this._idleDetectionInterval.totalMilliseconds);
    }

    private _stopIdleDetection() {
        this._disposeIdleDetectionTimer();
        if (this._userActivityDetector) this._userActivityDetector.dispose();
        this._isIdleRecToasterDisplayed = false;
    }

    private _closeToasterWhenOpeningAnotherGame() {
        if (this._isIdleRecToasterDisplayed && this._toasterDisposer) {
            this._onCloseAnalytics = this._openedAnotherGameWhileIdleIsActiveAnalytics;
            this._toasterDisposer();
            this._toasterDisposer = null;
        }
    }

    private _startUserActivityDetection() {
        this._userActivityDetector = this.services.sdk.createUserActivityDetactor({
            userActivityDetectionInterval: ACTIVITY_DETECTION_INTERVAL,
            onUserActivityCallback: this._resetIdleDetectionTimer.bind(this),
        })
    }

    // We have two different types of close analytics: one for clicking the idle rec's X btn and one for launching a game (NOT the one in idle) while the idle is active.
    private _defaultOnCloseAnalytics = () => {
        this._sendAnalytics({ action: 'click_close_button', label: this._recommendedGameType });
    };

    private _openedAnotherGameWhileIdleIsActiveAnalytics = () => {
        this._sendAnalytics({ action: 'launched_other_game', label: this._recommendedGameType });
    };

    private _openedRecommendedGameAnalytics = () => {
        this._sendAnalytics({ action: 'click_play', label: this._recommendedGameType });
    };

    private _onCloseAnalytics = this._defaultOnCloseAnalytics;

    private _onOpened() {
        this._isIdleRecToasterDisplayed = true;
    }

    private _onClosed() {
        this._isIdleRecToasterDisplayed = false;
        this._resetIdleDetectionTimer();
        this._onCloseAnalytics();
    }

    private async _showIdleRecommendationToaster() {
        if (!this.services.configuration.servicesConfigs.IdleRecommendations.featureEnabled) {
            return;
        }

        if (
            this._isIdleRecToasterDisplayed ||
            Boolean((await this.services.gameRunner)?.currentRunningGame) ||
            this.services.overlay.isOpen
        ) {
            return;
        }

        if (this.services.arenas.getCurrentArena()?.arenaName === 'videoSlidesArena') {
            return;
        }

        if (this._numberOfAppearancesInSession >= this._maxAppearancesAllowedPerSession) {
            this.services.logger.debug(`IdleRecommendations toaster not displayed - max appearances per session - ${this._maxAppearancesAllowedPerSession} reached`);
            return;
        }

        this._getRecentlyPlayedGame()
            .then(recentlyPlayedGame => this._getRecommendedGameBasedOnCorrelation(recentlyPlayedGame))
            .then(recomGameType => {
                this._recommendedGameType = recomGameType;
                return this._getRecommendedGameInfo(recomGameType);
            })
            .then(recomGameTypeInfo => this._showToaster(recomGameTypeInfo));
    }

    private async _showToaster(gameInfo: GameInfo): Promise<void> {
        this._onCloseAnalytics = this._defaultOnCloseAnalytics;

        const toasterHandler = this.showToast({
            toastOptions: {
                timeout: TimeSpan.fromSeconds(this._displayInterval.totalSeconds),
                hideProgressBar: true,
            },
            contentProps: {
                gameType: gameInfo.gameType,
                onPlayRecommendedGameHandler: () => {
                    if (this._isIdleRecToasterDisplayed && this._toasterDisposer) {
                        this._onCloseAnalytics = this._openedRecommendedGameAnalytics;
                        this._toasterDisposer();
                        this._toasterDisposer = null;
                    }
                    gameInfo.open({ isRealMode: true });
                }
            },
            onToastShown: () => {
                this._onOpened();
                this._increaseAppearancesInSession();
                this._sendAnalytics({ action: 'view', label: this._recommendedGameType, value: 1 + this._numberOfAppearancesInSession });
            },
            onToastClosed: () => {
                this._onClosed();
            }
        });
        this._toasterDisposer = toasterHandler?.close;
    }

    private async _getRecentlyPlayedGame(): Promise<number> {
        const recentlyGamesPlayed = this.services.games.recentlyPlayedGames.gameTypes
        if (recentlyGamesPlayed.length === 0) {
            const aiGameTypesArray = this.services.aiNewEngine.aiNewRecommendedGames?.gameTypes

            if (Check.isArray(aiGameTypesArray) && aiGameTypesArray.length > 0) {
                return aiGameTypesArray[Math.floor(Math.random() * aiGameTypesArray.length)];
            } else {
                this.services.logger.debug(`IdleRecommendations toaster - not displayed because we didnt get any ai list game`);
                return Promise.reject(false);
            }
        } else {
            return recentlyGamesPlayed[0];
        }
    }

    private async _getRecommendedGameBasedOnCorrelation(recentlyGamePlayed: number): Promise<any> {
        const correlatedGames = await this.services.games.getCorrelatedGames(recentlyGamePlayed, 6);
        if (Check.isArray(correlatedGames) && correlatedGames.length === 0) {
            this.services.logger.debug(`IdleRecommendations toaster - not displayed because we didnt get correlated games for gameType ${recentlyGamePlayed}`);
            return Promise.reject(false);
        }
        return correlatedGames[Math.floor(Math.random() * correlatedGames.length)];
    }

    private async _getRecommendedGameInfo(gameType: number): Promise<GameInfo> {
        const gameDataInfo = await this.services.games.getGameInfo(gameType);
        if (!gameDataInfo) {
            this.services.logger.debug(`IdleRecommendations toaster - not displayed because we didnt get game infos for gameType: ' ${gameType}`);
            return Promise.reject(false);
        }
        return gameDataInfo;
    }

    private _setIdleRecommendationDataInLocalStorage(): void {
        this.services.localStorage.setItem(IDLE_RECOMMENDATIONS_LOCAL_STORAGE_KEY, JSON.stringify({ noOfAppearancesInSession: this._numberOfAppearancesInSession || 0 }))
    }

    private _getNoOfAppearancesInSessionFromLocalStorage(): number {
        let { noOfAppearancesInSession } = this.services.localStorage.getJson(IDLE_RECOMMENDATIONS_LOCAL_STORAGE_KEY) || 0
        if (!Check.isNumber(noOfAppearancesInSession)) {
            noOfAppearancesInSession = 0
        }

        return noOfAppearancesInSession
    }

    private _resetAppearancesInSession(): void {
        this._numberOfAppearancesInSession = 0;
        this._setIdleRecommendationDataInLocalStorage();
    }

    private _increaseAppearancesInSession(): void {
        this._numberOfAppearancesInSession++;
        this._setIdleRecommendationDataInLocalStorage();
    }

    private _sendAnalytics(analyticsData): void {
        this.services.analytics.sendOrbitAIFeatureEvent(
            'idleRecommendations', // Category
            analyticsData?.action,
            analyticsData?.label,
            analyticsData?.value
        );
    }
}